import React from 'react'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import ArrowUpwardRounded from '@mui/icons-material/ArrowUpwardRounded'
import { alpha } from '@mui/material/styles'
import { useSmartTranslation } from '../../../../generic/hooks/useSmartTranslation'
import { formatDuration } from '../duration'
import { timeVsTheoretical } from '../timeVsTheoretical'
import { raisedColor } from '../detailSurfaces'

// ADDENDUM 45 — la tarjeta «Tiempo de ejecución» del desplegable de una operación.
//
// ⚠️⚠️ SUSTITUYE A TRES CIFRAS SUELTAS (teórico, real, programado) que no se comparaban entre sí.
// Estaban las tres bien y no decían lo que el PO viene a mirar, que es CUÁNTO SE HA DESVIADO. La
// desviación existía —`Deviation not computable` salía debajo— pero como un cuarto renglón suelto.
//
// 📌 EL PROGRAMADO NO ES UN TRAMO DE LA BARRA, y es la decisión que más fácil sería equivocar.
// Teórico y desviación SUMAN el tiempo real, así que apilarlos dice algo verdadero. El programado
// es OTRA medida de lo mismo —lo que se planificó—, no una parte del real: meterlo en la pila
// haría que la barra sumara algo que no existe. Va en la leyenda, con su valor, sin tramo.
//
// ⚠️ Y LA LEYENDA SE PINTA ENTERA AUNQUE FALTEN VALORES. Un programado ausente sale como "sin
// informar" en vez de desaparecer: que no esté es un hecho sobre esta operación, y una leyenda que
// encoge obliga a recordar cuántas entradas debería tener (RN8).

// El color llega como función del tema (igual que los `sx` de alrededor) y se resuelve aquí: un
// `backgroundColor: <función>` no lo entiende emotion y el cuadrito saldría sin color.
const Swatch = ({ color }) => (
  <Box
    component='span'
    sx={(theme) => ({ width: 8, height: 8, borderRadius: '2px', backgroundColor: typeof color === 'function' ? color(theme) : color, display: 'inline-block', flexShrink: 0 })}
  />
)

const LegendItem = ({ testId, swatch, label, value }) => (
  <Stack direction='row' spacing={0.75} alignItems='center'>
    <Swatch color={swatch} />
    <Typography variant='caption' sx={{ color: 'text.secondary' }}>{label}</Typography>
    <Typography data-testid={testId} variant='caption' sx={{ fontWeight: 700, fontFamily: 'monospace' }}>{value}</Typography>
  </Stack>
)

// ⚠️⚠️ EL SIGNO VA EN LA CIFRA, y no es un adorno: sin él, «Desviación 30m 00s» se lee IGUAL
// tanto si se tardó media hora de más como de menos. El titular viejo lo resolvía con palabras
// («30m 00s under the theoretical»), y al mover el dato a una leyenda compacta esa distinción
// estuvo a punto de perderse — el mismo descuido que casi se lleva el destino del rechazo al
// subirlo a la fila.
//
// ⚠️ Se formatea el VALOR ABSOLUTO y el signo se antepone a mano porque `formatDuration` hace
// `Math.max(0, …)`: pasarle un delta negativo devuelve "0s", o sea que «tardó media hora menos»
// se leería como «sin desviación».
const textoDesviacion = (tiempo, notReported) => {
  if (!tiempo.computable) return notReported
  if (tiempo.deltaSeconds === 0) return formatDuration(0)
  return `${tiempo.deltaSeconds > 0 ? '+' : '−'}${formatDuration(Math.abs(tiempo.deltaSeconds))}`
}

const ExecutionTimeCard = ({ data = {} }) => {
  const { t_ } = useSmartTranslation()
  const notReported = t_('Not reported')
  const tiempo = timeVsTheoretical(data.theoreticalDurationSeconds, data.actualDurationSeconds)
  const sinReal = data.actualDurationSeconds === null || data.actualDurationSeconds === undefined || data.actualDurationSeconds === ''

  return (
    <Box
      data-testid='execution-time-card'
      sx={(theme) => ({ p: 2, borderRadius: 2, backgroundColor: raisedColor(theme), border: `1px solid ${alpha(theme.palette.text.primary, 0.08)}` })}
    >
      <Typography variant='caption' sx={{ display: 'block', fontWeight: 800, letterSpacing: 0.8, textTransform: 'uppercase', color: 'text.secondary', mb: 1 }}>
        {t_('Execution time')}
      </Typography>

      <Stack direction='row' spacing={2} alignItems='baseline' justifyContent='space-between' flexWrap='wrap'>
        <Stack direction='row' spacing={1} alignItems='baseline'>
          <Typography data-testid='execution-time-actual' variant='h4' sx={{ fontWeight: 800, fontFamily: 'monospace' }}>
            {sinReal ? notReported : formatDuration(data.actualDurationSeconds)}
          </Typography>
          {!sinReal && <Typography variant='body2' sx={{ color: 'text.secondary' }}>{t_('actual')}</Typography>}
        </Stack>
        {/* ⚠ La píldora del exceso SOLO con exceso. Una que dijera "0s sobre el teórico" en las
            operaciones sanas sería un aviso permanente, que es un aviso que nadie lee. */}
        {tiempo.computable && tiempo.over && (
          <Chip
            data-testid='execution-time-over'
            size='small'
            icon={<ArrowUpwardRounded sx={{ fontSize: '14px !important', color: 'warning.main !important' }} />}
            label={t_('{{amount}} over the theoretical', { amount: formatDuration(tiempo.deltaSeconds) })}
            sx={(theme) => ({ fontWeight: 700, color: 'warning.main', backgroundColor: alpha(theme.palette.warning.main, 0.12), border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}` })}
          />
        )}
      </Stack>

      {!tiempo.computable && (
        <Typography data-testid='execution-time-not-computable' variant='caption' sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
          {/* La misma clave que ya usaba la desviación suelta: no se puede comparar, y eso NO es
              "coincidió con el teórico". */}
          {t_('Deviation not computable')}
        </Typography>
      )}

      {tiempo.computable && (
        <Box data-testid='execution-time-bar' data-over={tiempo.over ? 'true' : 'false'} sx={(theme) => ({ display: 'flex', height: 10, borderRadius: 5, mt: 1.5, backgroundColor: alpha(theme.palette.text.primary, 0.12), overflow: 'hidden' })}>
          <Box data-testid='execution-time-bar-theoretical' sx={(theme) => ({ width: `${tiempo.theoreticalFraction * 100}%`, backgroundColor: theme.palette.success.main })} />
          <Box data-testid='execution-time-bar-deviation' sx={(theme) => ({ width: `${tiempo.deviationFraction * 100}%`, backgroundColor: theme.palette.warning.main })} />
        </Box>
      )}

      <Stack direction='row' spacing={2.5} flexWrap='wrap' sx={{ mt: 1.25, rowGap: 0.5 }}>
        <LegendItem
          testId='execution-time-theoretical'
          swatch={(theme) => theme.palette.success.main}
          label={t_('Theoretical')}
          value={data.theoreticalDurationSeconds === '' || data.theoreticalDurationSeconds === null || data.theoreticalDurationSeconds === undefined ? notReported : formatDuration(data.theoreticalDurationSeconds)}
        />
        <LegendItem
          testId='execution-time-deviation'
          swatch={(theme) => theme.palette.warning.main}
          label={t_('Deviation')}
          value={textoDesviacion(tiempo, notReported)}
        />
        <LegendItem
          testId='execution-time-scheduled'
          swatch={(theme) => alpha(theme.palette.text.primary, 0.3)}
          label={t_('Scheduled')}
          value={data.scheduledDurationSeconds === '' || data.scheduledDurationSeconds === null || data.scheduledDurationSeconds === undefined ? notReported : formatDuration(data.scheduledDurationSeconds)}
        />
      </Stack>
    </Box>
  )
}

export default ExecutionTimeCard
